import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import ConnectedProjectView from "../../components/ConnectedProjectView";
import AppShell from "../../components/AppShell";
import PageHeader from "../../components/PageHeader";
import api from "../../api";
import "./MyProjects.css";

const MyProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/participations/my-projects");
      const nextProjects = response.data?.success
        ? response.data.data || []
        : Array.isArray(response.data)
          ? response.data.filter(Boolean)
          : [];
      setProjects(nextProjects);
    } catch (requestError) {
      console.error("Error fetching projects:", requestError);
      setError(
        requestError.response?.data?.error ||
          "We could not load your projects. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
    window.addEventListener("wcco:projects-updated", fetchProjects);
    return () => window.removeEventListener("wcco:projects-updated", fetchProjects);
  }, [fetchProjects]);

  const filteredProjects = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return projects;
    return projects.filter((project) =>
      [
        project?.name,
        project?.description,
        project?.organiserName,
        project?.organiserType,
        ...(project?.categories || []),
      ].some((field) => field?.toLowerCase().includes(query))
    );
  }, [projects, searchTerm]);

  return (
    <AppShell>
      <PageHeader
        title="My projects"
        description="Review your participation, volunteer hours, evidence, and reflections."
      />

      <div className="student-project-toolbar">
        <label className="visually-hidden" htmlFor="project-search">Search my projects</label>
        <FaSearch aria-hidden="true" />
        <input
          id="project-search"
          type="search"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search my projects"
        />
        <span>{filteredProjects.length} of {projects.length}</span>
      </div>

      {loading ? (
        <div className="student-project-state" role="status">
          <Spinner animation="border" size="sm" />
          <span>Loading your projects…</span>
        </div>
      ) : error ? (
        <Alert>{error}</Alert>
      ) : filteredProjects.length ? (
        <ConnectedProjectView projects={filteredProjects} />
      ) : (
        <div className="student-project-state">
          <h2>{searchTerm ? "No matching projects" : "No projects yet"}</h2>
          <p>{searchTerm ? "Try a different project name or category." : "Browse projects to join a volunteering programme."}</p>
        </div>
      )}
    </AppShell>
  );
};

export default MyProjects;
